import React from "react";

/**
 * GiftIcon — renders a gift's custom image OR emoji fallback, honoring the
 * per-gift animation controls (Phase D).
 *
 * Props:
 *   gift   — catalog row OR a `gift_snapshot` from a transaction. Fields used:
 *              { image_url, image_mime, emoji, animation_class,
 *                animation_speed_ms, animation_scale_pct, loop_count }
 *   size   — pixel size of the icon. Default 32.
 *   className — extra classes for the wrapper span.
 *   paused — set true to freeze the animation.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolveImgSrc(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND}${url}`;
}

export default function GiftIcon({ gift, size = 32, className = "", paused = false }) {
  if (!gift) return null;
  const cls = gift.animation_class || "gift-bounce";
  const scale = Number(gift.animation_scale_pct || 100) / 100;
  const durationMs = Number(gift.animation_speed_ms || 1200);
  const loop = Number(gift.loop_count || 0);
  const iterationCount = loop === 0 ? "infinite" : loop;
  const animStyle = paused
    ? {}
    : {
        animationDuration: `${durationMs}ms`,
        animationIterationCount: iterationCount,
      };
  const imgSrc = resolveImgSrc(gift.image_url);
  const testId = `gift-icon-${gift.gift_id || gift.name?.toLowerCase()?.replace(/\s+/g, "-") || "unknown"}`;
  return (
    <span
      className={`inline-block leading-none ${paused ? "" : cls} ${className}`}
      style={{
        fontSize: `${size}px`,
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: "center",
        display: "inline-block",
        ...animStyle,
      }}
      title={gift.name || ""}
      data-testid={testId}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={gift.name || "gift"}
          width={size}
          height={size}
          style={{ width: size, height: size, objectFit: "contain", display: "block" }}
          draggable={false}
          data-testid={`${testId}-img`}
        />
      ) : (
        gift.emoji || "🎁"
      )}
    </span>
  );
}
